import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext, useState } from 'react';
import styled, { css } from 'styled-components';
import { useCopyToClipboard } from '../../../utils/copy';
import { GlobalContext } from '../../contexts/GlobalContext';
import Alert from '../base/Alert';
import Button from '../base/Button';
import { StyledCard } from '../base/Card';
import Icon from '../base/Icon';
import Text from '../base/Text';
import TextInput from '../Form/Fields/TextInput';
import { Div } from '../helpers/StyledUtils';
import Modal from '../Modal';
import { useToasts } from '../Toast';
interface Props {
  count: number;
  onCodesGenerated: () => void;
}
interface BackupCodesDisplayProps {
  codes: string[];
  onClose: () => void;
  /** Modal width; defaults to medium. */
  size?: React.ComponentProps<typeof Modal>['size'];
}
const StyledBackupCodesGrid = styled.div(({
  theme
}) => css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing(2)};
    margin: ${theme.spacing(4)} 0;
  `);
const StyledBackupCode = styled.div(({
  theme
}) => css`
    font-family: 'JetBrains Mono', monospace;
    font-size: ${theme.pxToRem(theme.font_sizes.m.font_size)};
    padding: ${theme.spacing(2)} ${theme.spacing(3)};
    background-color: ${theme.colors.surface.base.variant_surface_2};
    border-radius: ${theme.radius.small};
    text-align: center;
  `);
export const BackupCodesDisplay: React.FC<BackupCodesDisplayProps> = t0 => {
  const $ = _c(17);
  const {
    codes,
    onClose,
    size: t1
  } = t0;
  const size = t1 === undefined ? "medium" : t1;
  const copyToClipboard = useCopyToClipboard();
  let t2;
  if ($[0] !== codes || $[1] !== copyToClipboard) {
    t2 = () => {
      const codes_text = codes.join("\n");
      copyToClipboard(codes_text);
    };
    $[0] = codes;
    $[1] = copyToClipboard;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleCopyAll = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      b: 4
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Alert inline={true} warning={true} m={t3}><Text size="s" flex={{
        align: "flex-start",
        gap: 2
      }}>Save these codes in a secure location. You won't be able to see them again. Each code can only be used once.</Text></Alert>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Text m={{
      b: 2
    }}>Use these backup codes to sign in if you lose access to your authenticator app.</Text>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== codes) {
    t6 = codes.map(_temp);
    $[6] = codes;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t6) {
    t7 = <StyledBackupCodesGrid>{t6}</StyledBackupCodesGrid>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== handleCopyAll) {
    t8 = <Button outline={true} block={true} icon="copy" onClick={handleCopyAll}>Copy all codes</Button>;
    $[10] = handleCopyAll;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== onClose || $[13] !== size || $[14] !== t7 || $[15] !== t8) {
    t9 = <Modal title="Save your backup codes" portal={true} onClose={onClose} submit_label="Done" onSubmit={onClose} size={size}>{t4}{t5}{t7}{t8}</Modal>;
    $[12] = onClose;
    $[13] = size;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
};
const BackupCodes: React.FC<Props> = ({
  count,
  onCodesGenerated
}) => {
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const [show_regenerate_modal, setShowRegenerateModal] = useState(false);
  const [new_codes, setNewCodes] = useState<string[] | null>(null);
  const [is_generating, setIsGenerating] = useState(false);
  const handleRegenerate = async (password: string) => {
    setIsGenerating(true);
    try {
      const response = await HookdeckAPI.multi_factor.generateBackupCodes(password);
      setNewCodes(response.backup_codes);
      setShowRegenerateModal(false);
      onCodesGenerated();
    } catch (e: any) {
      addToast('error', e.response?.message || 'Failed to generate backup codes');
    } finally {
      setIsGenerating(false);
    }
  };
  return <>
      <StyledCard flex={{
      align: 'center',
      justify: 'space-between'
    }} p={{
      x: 4,
      y: 3
    }}>
        <Div>
          <Text flex={{
          align: 'center'
        }}>
            <Icon icon="key" left />
            Backup codes
          </Text>
          <Text size="xs" muted m={{
          t: 1
        }}>
            {count} {count === 1 ? 'code' : 'codes'} remaining
          </Text>
        </Div>
        <Button outline small onClick={() => setShowRegenerateModal(true)}>
          Regenerate
        </Button>
      </StyledCard>

      {count <= 3 && count > 0 && <Alert inline warning m={{
      t: 2
    }}>
          <Text size="s">You're running low on backup codes. Consider regenerating new ones.</Text>
        </Alert>}

      {count === 0 && <Alert inline danger m={{
      t: 2
    }}>
          <Text size="s">
            You have no backup codes remaining. Regenerate new ones to ensure you can still access
            your account if you lose your authenticator.
          </Text>
        </Alert>}

      {show_regenerate_modal && <Formik initialValues={{
      password: ''
    }} validate={values => {
      const errors: {
        password?: string;
      } = {};
      if (!values.password) {
        errors.password = 'Password is required';
      }
      return errors;
    }} onSubmit={values_0 => handleRegenerate(values_0.password)}>
          {props => <Modal title="Regenerate backup codes" portal onClose={() => setShowRegenerateModal(false)} onCancel={() => setShowRegenerateModal(false)} cancel_label="Cancel" submit_label="Regenerate" is_submitting={is_generating} submit_disabled={!props.isValid || !props.values.password} onSubmit={props.submitForm}>
              <Form>
                <Alert inline warning m={{
            b: 4
          }}>
                  <Text size="s">
                    This will invalidate all existing backup codes and generate new ones.
                  </Text>
                </Alert>
                <TextInput label="Confirm your password" name="password" type="password" auto_complete="current-password" required focus />
              </Form>
            </Modal>}
        </Formik>}

      {new_codes && <BackupCodesDisplay codes={new_codes} onClose={() => setNewCodes(null)} />}
    </>;
};
export default BackupCodes;
function _temp(code, index) {
  return <StyledBackupCode key={index}>{code}</StyledBackupCode>;
}